import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SvgIconsModule } from '@ngneat/svg-icon';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { OnmatComponentHocModule } from '@tekman/onmat/component/hoc';
import { EnvironmentService, UserService } from '@tekman/onmat/service/generic';
import { AuthInterceptor, FeaturitWrapperService } from '@tekman/onmat/service/onmat';
import { OnmatStoreProfileModule, ProfileFacade } from '@tekman/onmat/store/profile';
import { OnmatStoreCoursesModule } from '@tekman/onmat/store/courses';
import { SharedComponentModule } from '@tekman/shared/component/shared';
import { SharedUtilsModule } from '@tekman/shared/utils';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header.component';
import { AccessTokenInterceptor } from './groups/service/access-token.interceptor';
import { SharedComponentGenericModule } from '@tekman/shared/component/generic';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { FeaturitModule } from 'featurit-sdk-angular';

function initializeKeycloak(
  keycloak: KeycloakService,
  environmentService: EnvironmentService,
  _http: HttpClient,
  featuritService: FeaturitWrapperService,
  userService: UserService
) {
  return async () => {
    const config: any = await _http.get('./environments/settings.json').toPromise();
    config['production'] = environment.production;
    environmentService.init(config);
    await keycloak.init({
      config: {
        url: config.authentication.authority,
        clientId: config.authentication.clientId,
        realm: config.authentication.realm,
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/auth/silent-check-sso.html',
      },
    });
    await userService.loaduser();

    const user = userService.getUser();
    if (user) {
      featuritService.init(user.attributes['coreId'] as string, config.featurit.frontendApiKey);
    }
  };
}

function httpTranslateLoader(_http: HttpClient) {
  return new TranslateHttpLoader(_http);
}

@NgModule({
  declarations: [AppComponent, HeaderComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    OnmatStoreProfileModule,
    OnmatStoreCoursesModule,
    MatSnackBarModule,
    SharedUtilsModule,
    SharedComponentModule,
    MatFormFieldModule,
    MatInputModule,
    SharedComponentGenericModule,
    OnmatComponentHocModule,
    KeycloakAngularModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoader,
        deps: [HttpClient],
      },
    }),
    FeaturitModule.forRoot({
      tenantIdentifier: 'tekman',
      frontendApiKey: '',
      refreshIntervalMinutes: 5,
      sendAnalyticsIntervalMinutes: 1,
      enableAnalytics: false,
      enableTracking: false,
    }),
    SvgIconsModule.forRoot({
      sizes: {
        xs: '0.875rem',
        sm: '1rem',
        md: '1.2rem',
        lg: '1.5rem',
        xl: '2.2rem',
        xxl: '2.4rem',
        exl: '2.9rem',
      },
      defaultSize: 'md',
      icons: [],
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      deps: [KeycloakService, EnvironmentService, HttpClient, FeaturitWrapperService, UserService],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AccessTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
